import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TableSortLabel,
} from "@mui/material";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table";
import Pagination from "../pagination/Pagination";
import { useState, useRef, useEffect } from "react";
import { isEven } from "../../../utils/helper";
// import Sortable from "../../sortable/Sortable";

const GROUP_ROW_BACKGROUND = "#edf2fb";

// Re-renders a body row only when its data, position, expansion, visible
// columns or caller-supplied key change.
const MemoizedBodyRow = React.memo(
  ({ row, render }) => render(row),
  (prev, next) =>
    prev.row.original === next.row.original &&
    prev.row.index === next.row.index &&
    prev.row.depth === next.row.depth &&
    prev.isExpanded === next.isExpanded &&
    prev.memoKey === next.memoKey &&
    prev.columns === next.columns &&
    prev.visibleColumnsKey === next.visibleColumnsKey
);

const ReusableTable = ({
  columns,
  data,
  paginationData,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  onSortingChange = () => {},
  footer = false,
  pagination = true,
  customBoxStyles,
  customTableStyles,
  customPaginationStyle,
  firstColumnStyle,
  customTableCellStyle = () => {},
  actions,
  maxHeight = "calc(100vh - 250px)",
  lastColumnEnd = true,
  enableSorting = true,
  multiRowFooter = false,
  customCellStyle = {},
  customEndCellStyle = {},
  fixedColumnIndex = [],
  noRecordColspan,
  lastColumnFixedIndex = [],
  maxWidth,
  tableStyle = {},
  customCellStyleFun = () => {},
  renderSubComponent = () => {},
  getRowCanExpand,
  isSubTable = true,
  defaultExpanded = false,
  compactSubRows = true,
  groupHeaderContentColumnId = "ro_number",
  fixedColumnLeftDist = 130,
  subtable_span = 8,
  onExpand = () => {},
  resetExpandedOnDataChange = true, // New prop to control this behavior
  /** When this value changes, expanded row state is cleared (e.g. filter / pill changes). */
  expandedResetKey,
  /** Stable row IDs keep expansion state aligned when list data is replaced (e.g. after inline edits). */
  getRowId,
  norecordStyle = {},
  customHeaderCellStyle = {},
  initialSorting = [],
  onRowClick,
  isRowClickable,
  /** Skip re-rendering unchanged rows. Cells must not read changing render-time state outside row.original unless it is part of getRowMemoKey. */
  memoizeRows = false,
  getRowMemoKey,
  /** While true, inline-edit cells (data-inline-edit) except the saving one are dimmed and not clickable. Done in CSS so rows don't re-render. */
  editLocked = false,
}) => {
  const [sorting, setSorting] = useState(initialSorting);
  const [expanded, setExpanded] = useState(() =>
    defaultExpanded ? true : {}
  );

  const tableContainerRef = useRef();
  const prevDataRef = useRef();

  // Reset expanded state when data changes (pagination/filtering)
  useEffect(() => {
    if (resetExpandedOnDataChange && prevDataRef.current !== data) {
      setExpanded({});
      prevDataRef.current = data;
    }
  }, [data, resetExpandedOnDataChange]);

  useEffect(() => {
    if (expandedResetKey !== undefined) {
      setExpanded({});
    }
  }, [expandedResetKey]);
  const table = useReactTable({
    data: data || [],
    columns,
    ...(typeof getRowId === "function" ? { getRowId } : {}),
    meta: {
      ...actions,
      onExpand: ({ row, expanded }) => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = 0;
        }
        onExpand({ row, expanded });
      },
    },
    state: {
      sorting,
      expanded,
    },
    enableSorting: enableSorting,
    enableMultiSort: false,
    manualSorting: false,
    sortDescFirst: true,
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      if (newSorting?.length === 0) {
        setSorting((prew) => {
          onSortingChange([{ id: prew[0]?.id, desc: !prew[0]?.desc }]);
          return [{ id: prew[0]?.id, desc: !prew[0]?.desc }];
        });
      } else {
        setSorting(newSorting);
        onSortingChange(newSorting);
      }
    },
    onExpandedChange: setExpanded,
    getRowCanExpand,
    getSubRows: (row) => (isSubTable ? undefined : row.subRows),
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const renderHeaderCell = (header, index, headerGroup) => {
    const canSort = header.column.getCanSort();
    const sortDirection = header.column.getIsSorted();
    const isFixedColumn = fixedColumnIndex.includes(index);
    const isLastFixedColumn = lastColumnFixedIndex.includes(index);
    const rowSpan = header.column.columnDef.meta?.rowSpan || 1;
    const hasSubColumns = header.column.columnDef.columns?.length > 0;
    const isRowSpanned = header.column.columnDef.meta?.enableRowSpan;
    const colSpan = hasSubColumns
      ? header.column.columnDef.columns.length
      : header.colSpan;

    if (isRowSpanned && headerGroup.depth > 0) {
      return null;
    }
    return (
      <TableCell
        key={header.id}
        colSpan={colSpan}
        rowSpan={rowSpan}
        sx={{
          top: `${headerGroup.depth * 41.6}px`,
          left: isFixedColumn ? `${index * fixedColumnLeftDist}px` : "auto",
          zIndex: isFixedColumn ? 3 : 2,
          height: "37px",
          whiteSpace: "nowrap",
          p: "8px",
          color: header.column.getIsSorted()
            ? "#00a2b4 !important"
            : "var(--primary-color)",
          fontSize: "var(--font-size-13)",
          fontWeight: "var(--font-semibold)",
          borderBottom: "1px solid var(--alto-color)",
          backgroundColor: "var(--blackhaze-color)",
          textAlign: hasSubColumns ? "center" : "left",
          verticalAlign: rowSpan > 1 ? "middle" : "baseline",
          ...(isLastFixedColumn ? { right: "0px" } : {}),
          ...customCellStyle,
          ...customCellStyleFun(header),
          ...customHeaderCellStyle,
        }}
        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
      >
        {
          <>
            {flexRender(header.column.columnDef.header, header.getContext())}
            {canSort && !hasSubColumns && (
              <TableSortLabel
                active={true}
                sx={{
                  color: header.column.getIsSorted()
                    ? "#00a2b4 !important"
                    : "inherit",
                  "& .MuiTableSortLabel-icon": {
                    color: header.column.getIsSorted()
                      ? "#00a2b4 !important"
                      : "inherit",
                  },
                }}
                direction={sortDirection || "asc"}
              />
              // <Sortable sorted={!!sortDirection} direction={sortDirection} />
            )}
          </>
        }
      </TableCell>
    );
  };

  const renderBodyCell = (cell, row, index) => {
    const isFixedColumn = fixedColumnIndex.includes(index);
    const isLastFixedColumn = lastColumnFixedIndex.includes(index);

    return (
      <TableCell
        key={cell.id}
        sx={{
          position:
            isFixedColumn || isLastFixedColumn ? "sticky" : "relative",
          left: isFixedColumn ? `${index * fixedColumnLeftDist}px` : "auto",
          right: isLastFixedColumn ? `0px` : "auto",
          zIndex: isFixedColumn ? 2 : 1,
          backgroundColor:
            isLastFixedColumn || isFixedColumn
              ? isEven(row?.index)
                ? "var(--secondary-color)"
                : "var(--concrete-color)"
              : "inherit",
          transition: "background-color 0.2s ease",
          ...customTableCellStyle(cell),
        }}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  };

  const renderGroupHeaderRow = (row) => {
    const visibleCells = row.getVisibleCells();
    const leadingColumnIds = ["expand", "row_icon"];
    const leadingCells = visibleCells.filter((cell) =>
      leadingColumnIds.includes(cell.column.id)
    );
    const contentCell =
      visibleCells.find((cell) => cell.column.id === groupHeaderContentColumnId) ||
      visibleCells.find((cell) => !leadingColumnIds.includes(cell.column.id));
    const trailingColSpan = Math.max(
      visibleCells.length - leadingCells.length,
      1
    );

    return (
      <TableRow
        sx={{
          height: "37px",
          backgroundColor: `${GROUP_ROW_BACKGROUND} !important`,
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: `${GROUP_ROW_BACKGROUND} !important`,
          },
          "&:hover td": {
            backgroundColor: `${GROUP_ROW_BACKGROUND} !important`,
          },
          "& td": {
            overflow: "hidden",
            color: "var(--primary-color)",
            fontSize: "var(--font-size-13)",
            fontWeight: "var(--font-semibold)",
            p: "8px",
            whiteSpace: "nowrap",
            borderBottom: "1px solid var(--alto-color)",
            ...customCellStyle,
          },
        }}
      >
        {leadingCells.map((cell, index) => (
          <TableCell
            key={cell.id}
            sx={{
              position: fixedColumnIndex.includes(index) ? "sticky" : "relative",
              left: fixedColumnIndex.includes(index)
                ? `${index * fixedColumnLeftDist}px`
                : "auto",
              zIndex: fixedColumnIndex.includes(index) ? 2 : 1,
              backgroundColor: `${GROUP_ROW_BACKGROUND} !important`,
              borderBottom: "1px solid var(--alto-color)",
              p: "8px",
              ...customTableCellStyle(cell),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
        <TableCell
          colSpan={trailingColSpan}
          sx={{
            backgroundColor: `${GROUP_ROW_BACKGROUND} !important`,
            borderBottom: "1px solid var(--alto-color)",
            p: "8px",
            ...customTableCellStyle({ row, column: contentCell?.column }),
          }}
        >
          {contentCell
            ? flexRender(
                contentCell.column.columnDef.cell,
                contentCell.getContext()
              )
            : null}
        </TableCell>
      </TableRow>
    );
  };

  const renderDataRow = (row) => {
    const isClickable =
      onRowClick && (!isRowClickable || isRowClickable(row));

    return (
      <>
        <TableRow
          onClick={isClickable ? () => onRowClick({ row }) : undefined}
          sx={{
            height: "37px",
            cursor: isClickable ? "pointer" : "default",
            backgroundColor: isEven(row?.index)
              ? "var(--secondary-color)"
              : "var(--concrete-color)",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: "var(--hawkes-color)",
            },
            "&:hover td": {
              backgroundColor: "var(--hawkes-color) !important",
            },
            "&:last-child td, &:last-child th": {
              borderBottom: 0,
            },
            "& td": {
              overflow: "hidden",
              color: "var(--primary-color)",
              fontSize: "var(--font-size-13)",
              fontWeight: "var(--font-regular)",
              p: "8px",
              whiteSpace: "nowrap",
              ...customCellStyle,
            },
            "& td:last-of-type, & th:last-of-type": {
              textAlign: lastColumnEnd ? "end" : "start",
              whiteSpace: "nowrap",
              ...customEndCellStyle,
            },
            ...firstColumnStyle,
            ...(compactSubRows &&
              row.depth > 0 && {
                "& td": {
                  paddingLeft: "15px",
                  paddingY: "0px",
                  fontSize: "var(--font-size-11)",
                  color: "var(--primary-color)",
                  backgroundColor: isEven(row?.parentId)
                    ? "var(--secondary-color) !important"
                    : "var(--concrete-color) !important",
                  ...customCellStyle,
                },
                "&:hover": {
                  backgroundColor: "var(--hawkes-color)",
                },
                "&:hover td": {
                  backgroundColor: "var(--hawkes-color) !important",
                },
              }),
          }}
        >
          {row.getVisibleCells().map((cell, index) =>
            renderBodyCell(cell, row, index)
          )}
        </TableRow>
        {row.getIsExpanded() && isSubTable && (
          <TableRow
            sx={{
              backgroundColor: isEven(row?.index)
                ? "var(--secondary-color)"
                : "var(--concrete-color)",
            }}
          >
            <TableCell colSpan={subtable_span}>
              {renderSubComponent({ row })}
            </TableCell>
            <TableCell colSpan={columns?.length - subtable_span}></TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const visibleColumnsKey = memoizeRows
    ? table
        .getVisibleLeafColumns()
        .map((col) => col.id)
        .join("|")
    : "";

  return (
    <Box sx={{ ...customBoxStyles, height: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          border: "1px solid var(--alto-color)",
          borderRadius: "0",
          boxShadow: "none",
          display: "flex",
          maxWidth: maxWidth,
          flexDirection: "column",
          maxHeight: maxHeight,
          overflow: "auto",
          ...(editLocked && {
            '& [data-inline-edit]:not([data-inline-edit-saving="true"])': {
              opacity: 0.7,
              pointerEvents: "none",
            },
          }),
          ...customTableStyles,
        }}
        ref={tableContainerRef}
      >
        <Table stickyHeader sx={tableStyle}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <TableRow
                key={headerGroup.id}
                sx={{
                  backgroundColor: "var(--blackhaze-color)",
                  fontVariantLigatures: "none",
                  ...firstColumnStyle,
                  "& td:last-of-type, & th:last-of-type": {
                    textAlign: lastColumnEnd && index === 0 ? "end" : "start",
                    ...customEndCellStyle,
                  },
                }}
              >
                {headerGroup.headers.map((header, index) =>
                  renderHeaderCell(header, index, headerGroup)
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {data && data.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                if (row.original?._isGroupRow) {
                  return (
                    <React.Fragment key={row.id}>
                      {renderGroupHeaderRow(row)}
                    </React.Fragment>
                  );
                }

                if (memoizeRows) {
                  return (
                    <MemoizedBodyRow
                      key={row.id}
                      row={row}
                      render={renderDataRow}
                      isExpanded={row.getIsExpanded()}
                      memoKey={getRowMemoKey ? getRowMemoKey(row) : ""}
                      columns={columns}
                      visibleColumnsKey={visibleColumnsKey}
                    />
                  );
                }

                return (
                  <React.Fragment key={row.id}>
                    {renderDataRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={noRecordColspan ? noRecordColspan : columns.length}
                  sx={{
                    textAlign: "center",
                    py: 8,
                    color: "var(--primary-color)",
                    fontSize: "var(--font-size-14)",
                    fontWeight: "var(--font-medium)",
                    backgroundColor: "var(--secondary-color)",
                    borderBottom: "none",
                    ...norecordStyle,
                  }}
                >
                  No Records Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {footer && data && data.length > 0 && (
            <TableFooter>
              {table.getFooterGroups().map((footerGroup) => (
                <>
                  <TableRow key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        sx={{
                          backgroundColor: "var(--blackhaze-color)",
                          color: "var(--primary-color)",
                          fontWeight: "var(--font-semibold)",
                          fontSize: "var(--font-size-13)",
                          borderTop: "1px solid var(--alto-color)",
                          p: "8px",
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {multiRowFooter &&
                    table.options.meta?.footerData?.footerRows?.map(
                      (footerRowData, index) => (
                        <TableRow key={`footer-row-${index}`}>
                          {footerGroup.headers.map((header) => {
                            const columnId = header.column.id;
                            return (
                              <TableCell
                                key={header.id}
                                colSpan={1}
                                sx={{
                                  backgroundColor: "var(--secondary-color)",
                                  color: "var(--primary-color)",
                                  fontWeight: "var(--font-regular)",
                                  fontSize: "var(--font-size-13)",
                                  p: "8px",
                                }}
                              >
                                {footerRowData[columnId] || ""}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    )}
                </>
              ))}
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {pagination && paginationData && data && data.length > 0 && (
        <Pagination
          customPaginationStyle={customPaginationStyle}
          page={paginationData.page - 1}
          totalPages={paginationData.total_pages}
          totalRows={paginationData.total}
          handleChangePage={(newPage) => onPageChange(newPage + 1)}
          rowsPerPage={paginationData.per_page}
          pagesOptions={paginationData?.pagesOptions}
          handleRowsPerPageChange={(event) =>
            onRowsPerPageChange(
              event.target.value === "All"
                ? paginationData.total
                : event.target.value
            )
          }
        />
      )}
    </Box>
  );
};

export default ReusableTable;
